/**
 * Money formatting for the billing surfaces.
 *
 * Everything here takes **integer minor units (centavos)**, because that is what
 * Stripe reports and what the DB stores. Taking pesos would invite a silent
 * divide-by-100 at each call site, and one missed division shows a school's fee as
 * 699,000.
 *
 * The currency is whatever Stripe attached to the charge — never hardcoded to MXN.
 * A foreign card can settle in another currency, and printing "$118.00" next to a
 * USD amount as though it were pesos is worse than printing nothing.
 */

// Locale is es-MX throughout the CRM (see format-day.ts, ScriptEditor.tsx).
const LOCALE = "es-MX";

// Intl.NumberFormat is expensive to construct; one per currency is plenty.
const formatters = new Map<string, Intl.NumberFormat>();

function formatterFor(currency: string): Intl.NumberFormat {
  const key = currency.toUpperCase();
  const cached = formatters.get(key);
  if (cached) return cached;
  const created = new Intl.NumberFormat(LOCALE, {
    style: "currency",
    currency: key,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  formatters.set(key, created);
  return created;
}

/**
 * Renders an amount in minor units, e.g. `formatMxn(699000)` → `"$6,990.00"`.
 *
 * Returns an em dash for null so callers can render the result directly — a
 * client with no subscription yet has no amount, and that is not an error state.
 */
export function formatMxn(
  amountCents: number | null | undefined,
  currency: string | null | undefined = "MXN",
): string {
  if (amountCents === null || amountCents === undefined) return "—";
  try {
    return formatterFor(currency ?? "MXN").format(amountCents / 100);
  } catch {
    // An unknown/invalid ISO code makes Intl throw. Degrade to the raw number plus
    // the code rather than blanking a real amount.
    return `${(amountCents / 100).toFixed(2)} ${(currency ?? "").toUpperCase()}`.trim();
  }
}

/**
 * Centavos → the pesos string a form input shows. `null` stays `""`.
 *
 * `""` and `"0.00"` are different answers and both are load-bearing wherever
 * this is used: the tenant charges nothing for delivery, versus delivery is
 * free. Never collapse them.
 */
export function centsToPesos(cents: number | null | undefined): string {
  // `undefined` is accepted and treated as `null`: this reads a JSON payload, so
  // a field an older backend does not send would otherwise become "NaN" — which
  // reads as dirty AND invalid, permanently disabling a whole Save button.
  return cents === null || cents === undefined ? "" : (cents / 100).toFixed(2);
}

/**
 * The pesos string → centavos, or `null` when it is not a usable amount.
 *
 * Hand-rolled rather than `parseFloat` alone because `parseFloat("80abc")` is 80:
 * it would silently accept a typo and charge a real customer for it. The regex
 * requires the WHOLE string to be a plain amount, and `Math.round` is what keeps
 * 80.10 from landing as 8009 through binary floating point.
 *
 * NOTE: `PaymentLinkModal` deliberately keeps its own, LENIENT converter. That
 * one strips currency formatting so a super-admin can paste "$6,990" out of a
 * deal note, and it returns a number rather than null. Different contract, not a
 * duplicate — do not "unify" them.
 */
export function pesosToCents(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const cents = Math.round(Number(trimmed) * 100);
  return Number.isFinite(cents) && cents >= 0 && cents <= 1_000_000 ? cents : null;
}
